import {useEffect, useRef} from "react";
import SockJS from "sockjs-client";
import {Client} from "@stomp/stompjs";
import {TOKEN} from "../context/SignInContext";
import {refreshConnectHeaders} from "../utils/authTokens";

// Same backend/connection pattern as useStompWebsocketKbManager, but subscribes to MULTIPLE
// destinations (one per topic) over a single shared STOMP connection, for as long as `destinations`
// is non-empty - not scoped to "while something is uploading" like the single-topic hook.
//
// Delivers messages via a callback ref instead of returning "the latest value" as state: with many
// topics subscribed at once, two messages can legitimately arrive in the same React batch, and a
// single-latest-value hook would silently lose the earlier one. A callback fires for every message.
//
// `destinations` should be a stable-ish array of STOMP destination strings; the connection only
// resets when the actual set of destinations changes (compared by sorted content, not reference).
export const useStompWebsocketKbManagerMulti = (destinations, onMessage) => {
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;

    const key = destinations && destinations.length > 0 ? [...destinations].sort().join("|") : null;

    useEffect(() => {
        if (!key) return;
        const dests = key.split("|");
        let stompClient = null;

        const wsBaseUrl = process.env.REACT_APP_KB_MANAGER_WS_URL;
        const socket = new SockJS(wsBaseUrl);
        const token = localStorage.getItem(TOKEN);

        stompClient = new Client({
            webSocketFactory: () => socket,
            connectHeaders: token ? {Authorization: `Bearer ${token}`} : {},
            beforeConnect: refreshConnectHeaders,
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
        });

        stompClient.onConnect = () => {
            dests.forEach(dest => {
                stompClient?.subscribe(dest, (message) => {
                    try {
                        const update = JSON.parse(message.body);
                        if (update) onMessageRef.current?.(update);
                    } catch (error) {
                        console.error("Error processing Knowledge Manager WebSocket message:", error);
                    }
                });
            });
        };

        stompClient.onStompError = (frame) => {
            console.error("Knowledge Manager STOMP error:", frame);
        };

        stompClient.activate();

        return () => {
            stompClient?.deactivate();
        };
    }, [key]);
};
